import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import IndeterminateCheckBoxRoundedIcon from "@mui/icons-material/IndeterminateCheckBoxRounded";
import { Box, Checkbox, Divider, Typography, useTheme } from "@mui/material";
import type { GridColumnHeadersProps } from "@mui/x-data-grid-premium";
import { GridColumnHeaders, Toolbar } from "@mui/x-data-grid-premium";
import type { CSSProperties, PropsWithChildren, ReactNode } from "react";
import { createContext, use } from "react";

export type DataGridToolbarSelection = [string[], (ids: string[]) => void];

interface ToolbarConfig {
  actions: ReactNode;
  selection?: DataGridToolbarSelection;
  fullSelection?: boolean;
  style?: CSSProperties;
}

interface ProviderProps extends ToolbarConfig {
  showToolbar: boolean;
}

const DataGridHeaderToolbarContext = createContext<ToolbarConfig | null>(null);

export const DataGridHeaderToolbarProvider = ({
  showToolbar,
  children,
  ...toolbar
}: PropsWithChildren<ProviderProps>) => (
  <DataGridHeaderToolbarContext value={showToolbar ? toolbar : null}>{children}</DataGridHeaderToolbarContext>
);

export const DataGridColumnHeadersWithToolbar = (props: GridColumnHeadersProps) => {
  const theme = useTheme();

  const toolbar = use(DataGridHeaderToolbarContext);

  const [selectedIds, setSelectedIds] = toolbar?.selection ?? [undefined, undefined];

  return (
    <Box sx={{ position: "relative" }}>
      <GridColumnHeaders {...props} />
      {toolbar && (
        <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bgcolor: "background.paper", zIndex: 100 }}>
          <Toolbar
            style={{
              minHeight: 36,
              height: 36,
              zIndex: 1,
              padding: 0,
              paddingLeft: 6,
              width: "100%",
              borderBottom: "1px solid",
              borderColor: theme.palette.divider,
              justifyContent: "start",
              backgroundColor: theme.palette.background.paper,
              ...toolbar.style,
            }}
          >
            {selectedIds && setSelectedIds && (
              <>
                <Checkbox
                  checked={!!toolbar.fullSelection}
                  indeterminate={!toolbar.fullSelection}
                  checkedIcon={<CheckBoxRoundedIcon />}
                  indeterminateIcon={<IndeterminateCheckBoxRoundedIcon />}
                  onClick={() => setSelectedIds([])}
                />
                <Typography variant="subtitle2">{selectedIds.length} selected</Typography>
                <Divider orientation="vertical" variant="middle" flexItem sx={{ ml: 2, mr: 1 }} />
              </>
            )}
            {toolbar.actions}
          </Toolbar>
        </Box>
      )}
    </Box>
  );
};
